import React, { useState, useRef } from 'react';
import FormImportExportService from '../services/formImportExportService';
import './FormImportExport.css';

const FormImportExport = ({ 
  user, 
  forms, 
  onImportComplete, 
  onClose,
  currentFormId = null 
}) => {
  const [activeTab, setActiveTab] = useState('export');
  const [exportType, setExportType] = useState('all');
  const [selectedFormId, setSelectedFormId] = useState(currentFormId || '');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [importOptions, setImportOptions] = useState({
    replacePasswords: true,
    generateNewSignature: true,
    preserveTitle: false
  });
  const fileInputRef = useRef();

  const handleExport = async () => {
    if (!user) return;
    
    setExporting(true);
    try {
      let exportData;
      let filename;
      
      if (exportType === 'single' && selectedFormId) {
        exportData = await FormImportExportService.exportForm(user.uid, selectedFormId);
        const formTitle = forms.find(f => f.id === selectedFormId)?.title || 'form';
        filename = `form-export-${formTitle.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
      } else if (exportType === 'all') {
        exportData = await FormImportExportService.exportAllForms(user.uid);
        filename = `all-forms-export-${new Date().toISOString().split('T')[0]}.json`;
      } else if (exportType === 'complete') {
        exportData = await FormImportExportService.createCompleteExport(user.uid);
        filename = `complete-export-${new Date().toISOString().split('T')[0]}.json`;
      }
      
      FormImportExportService.downloadAsJson(exportData, filename);
      
      // Show success message
      alert(`Export completed successfully! File downloaded as ${filename}`);
      
    } catch (error) {
      console.error('Export error:', error);
      alert(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        const validation = FormImportExportService.validateImportData(importData);
        setValidationResults(validation);
        
        if (!validation.valid) {
          alert(`Invalid import file: ${validation.errors.join(', ')}`);
          return;
        }
        
        // Show validation warnings if any
        if (validation.warnings.length > 0) {
          const proceed = window.confirm(
            `Import validation warnings:\n${validation.warnings.join('\n')}\n\nDo you want to continue with the import?`
          );
          if (!proceed) return;
        }
        
        handleImport(importData);
        
      } catch (error) {
        alert('Invalid JSON file. Please select a valid form export file.');
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async (importData) => {
    if (!user) return;
    
    setImporting(true);
    setImportResults(null);
    
    try {
      let results;
      
      switch (importData.type) {
        case 'single-form':
          const formId = await FormImportExportService.importForm(
            user.uid, 
            importData.form, 
            importOptions
          );
          results = {
            successful: [formId],
            errors: [],
            totalImported: 1,
            totalErrors: 0
          };
          break;
          
        case 'all-forms':
          results = await FormImportExportService.importMultipleForms(
            user.uid,
            importData.forms,
            importOptions
          );
          break;
          
        case 'complete-package':
          if (importData.package.forms) {
            results = await FormImportExportService.importMultipleForms(
              user.uid,
              importData.package.forms.forms,
              importOptions
            );
          }
          break;
          
        default:
          throw new Error(`Unsupported import type: ${importData.type}`);
      }
      
      setImportResults(results);
      
      // Refresh the forms list
      if (onImportComplete) {
        onImportComplete();
      }
      
      // Show results
      if (results.totalErrors === 0) {
        alert(`Import successful! ${results.totalImported} form(s) imported.`);
      } else {
        alert(`Import completed with warnings. ${results.totalImported} form(s) imported, ${results.totalErrors} error(s).`);
      }
      
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="import-export-modal">
      <div className="import-export-content">
        <div className="modal-header">
          <h2>Form Import/Export</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            📤 Export Forms
          </button>
          <button 
            className={`tab-btn ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            📥 Import Forms
          </button>
        </div>

        {activeTab === 'export' && (
          <div className="export-section">
            <h3>Export Your Forms</h3>
            <p>Download your forms and settings to transfer them to another account or create a backup.</p>
            
            <div className="export-options">
              <label className="radio-option">
                <input 
                  type="radio" 
                  value="all" 
                  checked={exportType === 'all'}
                  onChange={(e) => setExportType(e.target.value)}
                />
                <span>Export All Forms</span>
                <small>Download all your forms in one file</small>
              </label>
              
              <label className="radio-option">
                <input 
                  type="radio" 
                  value="single" 
                  checked={exportType === 'single'}
                  onChange={(e) => setExportType(e.target.value)}
                />
                <span>Export Single Form</span>
                <small>Download only one specific form</small>
              </label>
              
              {exportType === 'single' && (
                <select 
                  value={selectedFormId} 
                  onChange={(e) => setSelectedFormId(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select a form to export</option>
                  {forms.map(form => (
                    <option key={form.id} value={form.id}>
                      {form.title || 'Untitled Form'}
                    </option>
                  ))}
                </select>
              )}
              
              <label className="radio-option">
                <input 
                  type="radio" 
                  value="complete" 
                  checked={exportType === 'complete'}
                  onChange={(e) => setExportType(e.target.value)}
                />
                <span>Complete Backup</span>
                <small>Download forms and profile settings</small>
              </label>
            </div>
            
            <div className="export-actions">
              <button 
                className="export-btn"
                onClick={handleExport}
                disabled={exporting || (exportType === 'single' && !selectedFormId)}
              >
                {exporting ? '📥 Exporting...' : '📤 Download Export'}
              </button>
            </div>
            
            <div className="export-notes">
              <h4>📝 Export Notes:</h4>
              <ul>
                <li>🔒 Password protections will be cleared on import</li>
                <li>💳 Payment settings will need to be reconfigured</li>
                <li>🔢 Form signatures will be regenerated</li>
                <li>📊 Submissions are not included in exports</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'import' && (
          <div className="import-section">
            <h3>Import Forms</h3>
            <p>Upload a previously exported form file to import forms into this account.</p>
            
            <div className="import-options">
              <h4>Import Options:</h4>
              
              <label className="checkbox-option">
                <input 
                  type="checkbox" 
                  checked={importOptions.replacePasswords}
                  onChange={(e) => setImportOptions({
                    ...importOptions,
                    replacePasswords: e.target.checked
                  })}
                />
                <span>Clear password protections</span>
                <small>Recommended: Remove password protection from imported forms</small>
              </label>
              
              <label className="checkbox-option">
                <input 
                  type="checkbox" 
                  checked={importOptions.generateNewSignature}
                  onChange={(e) => setImportOptions({
                    ...importOptions,
                    generateNewSignature: e.target.checked
                  })}
                />
                <span>Generate new form signatures</span>
                <small>Recommended: Create new unique identifiers for forms</small>
              </label>
              
              <label className="checkbox-option">
                <input 
                  type="checkbox" 
                  checked={importOptions.preserveTitle}
                  onChange={(e) => setImportOptions({
                    ...importOptions,
                    preserveTitle: e.target.checked
                  })}
                />
                <span>Keep original titles</span>
                <small>Otherwise, "(Imported)" will be added to form titles</small>
              </label>
            </div>
            
            <div className="import-actions">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json"
                style={{ display: 'none' }}
              />
              
              <button 
                className="import-btn"
                onClick={triggerFileSelect}
                disabled={importing}
              >
                {importing ? '📥 Importing...' : '📁 Select Import File'}
              </button>
            </div>
            
            {validationResults && (
              <div className="validation-results">
                <h4>📋 File Validation:</h4>
                {validationResults.valid ? (
                  <div className="validation-success">✅ File format is valid</div>
                ) : (
                  <div className="validation-errors">
                    ❌ Validation errors:
                    <ul>
                      {validationResults.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {validationResults.warnings.length > 0 && (
                  <div className="validation-warnings">
                    ⚠️ Warnings:
                    <ul>
                      {validationResults.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {importResults && (
              <div className="import-results">
                <h4>📊 Import Results:</h4>
                <div className="result-stats">
                  <span className="success">✅ {importResults.totalImported} forms imported</span>
                  {importResults.totalErrors > 0 && (
                    <span className="errors">❌ {importResults.totalErrors} errors</span>
                  )}
                </div>
                
                {importResults.errors.length > 0 && (
                  <div className="import-errors">
                    <h5>Errors:</h5>
                    <ul>
                      {importResults.errors.map((error, index) => (
                        <li key={index}>Form {error.index + 1}: {error.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            <div className="import-notes">
              <h4>📝 Import Notes:</h4>
              <ul>
                <li>📁 Only JSON files exported from this app are supported</li>
                <li>🔄 Imported forms will have "(Imported)" added to their titles</li>
                <li>🆔 New unique form IDs will be generated</li>
                <li>⚠️ Some settings may need manual reconfiguration</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormImportExport;